import React, { useEffect, useRef, useState } from 'react';
import { removeUnitCoLauncher, restoreUnitCoLauncher } from '../../utils/unitcoLauncherCleanup';

interface UnitWhiteLabelAppProps {
  cardType?: string;
  jwtToken: string;
  currentUserId?: string | null; // Accept both string and null
  currentArtistId?: number | null; // Track artist changes
  themeUrl?: string;
  languageUrl?: string;
  onLoadComplete?: () => void;
}

const UnitWhiteLabelApp: React.FC<UnitWhiteLabelAppProps> = ({
  cardType = "",
  jwtToken,
  currentUserId: propCurrentUserId,
  currentArtistId,
  themeUrl,
  languageUrl,
  onLoadComplete
}) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  // CRITICAL: Remove refs that prevent re-initialization - use state instead or check props directly
  // Refs persist across renders and can cause stale checks
  const onLoadCompleteRef = useRef(onLoadComplete);
  const initializationKeyRef = useRef<string>(''); // Track current initialization key (artist + token hash)
  
  // Update ref when callback changes (but don't trigger re-initialization)
  useEffect(() => {
    onLoadCompleteRef.current = onLoadComplete;
  }, [onLoadComplete]);

  // Load Unit.co SDK and create component in a single useEffect
  // CRITICAL: This effect depends on jwtToken and currentArtistId to force re-initialization
  useEffect(() => {
    // CRITICAL: Create a unique key for this initialization (artist + token hash)
    // This ensures we always re-initialize when artist or token changes
    const tokenHash = jwtToken ? jwtToken.substring(0, 20) + jwtToken.substring(jwtToken.length - 10) : '';
    const currentInitKey = `${currentArtistId}-${propCurrentUserId}-${tokenHash}`;
    
    console.log('[UnitWhiteLabelApp] useEffect triggered - jwtToken exists:', !!jwtToken, 'currentArtistId:', currentArtistId);

    // Restore launcher visibility when (re)entering Banking
    restoreUnitCoLauncher();
    
    if (!propCurrentUserId || !jwtToken) {
      initializationKeyRef.current = '';
      return;
    }
    
    // CRITICAL: Check if initialization key changed (artist, user, or token changed)
    const initKeyChanged = initializationKeyRef.current !== currentInitKey;
    
    // If initialization key changed, force complete cleanup and re-initialization
    if (initKeyChanged) {
      console.log('🔄 [UnitWhiteLabelApp] Initialization key changed - forcing complete cleanup and re-initialization');
      
      // CRITICAL: Force complete cleanup of widget element
      if (containerRef.current) {
        const existingElement = containerRef.current.querySelector('unit-elements-white-label-app');
        if (existingElement) {
          console.log('🗑️ [UnitWhiteLabelApp] Removing existing widget element completely');
          
          // CRITICAL: Try to disconnect/dispose the widget before removing
          try {
            if ('disconnectedCallback' in existingElement && typeof (existingElement as any).disconnectedCallback === 'function') {
              (existingElement as any).disconnectedCallback();
            }
            
            // Remove all attributes to force widget to reset
            while (existingElement.attributes.length > 0) {
              existingElement.removeAttribute(existingElement.attributes[0].name);
            }
          } catch (e) {
            console.log('[UnitWhiteLabelApp] Error during element cleanup:', e);
          }
          
          // Remove the element
          existingElement.remove();
        }
        
        // CRITICAL: Clear innerHTML multiple times to ensure nothing remains
        containerRef.current.innerHTML = '';
        containerRef.current.innerHTML = ''; // Double clear
        
        // CRITICAL: Force a reflow to ensure DOM is updated
        void containerRef.current.offsetHeight;
      }
      
      // Update initialization key AFTER cleanup
      initializationKeyRef.current = currentInitKey;
    } else {
      // Same initialization key - check if element already exists
      if (containerRef.current) {
        const existingElement = containerRef.current.querySelector('unit-elements-white-label-app');
        if (existingElement) {
          // Verify the element has the correct JWT token
          const elementToken = existingElement.getAttribute('jwt-token');
          if (elementToken === jwtToken) {
            console.log('⏭️ [UnitWhiteLabelApp] Widget already initialized with correct token, skipping');
            return;
          } else {
            console.log('⚠️ [UnitWhiteLabelApp] Widget exists but token mismatch');
            // Token mismatch - force cleanup
            existingElement.remove();
            containerRef.current.innerHTML = '';
            initializationKeyRef.current = ''; // Reset to force re-initialization
          }
        }
      }
    }
    
    try {
      // Mock Zendesk to prevent Unit.co errors
      if (!window.zE) {
        window.zE = function() {
          // Mock Zendesk function - do nothing
        };
      }
      
      const scriptId = 'unit-wl-script';
      const env = process.env.REACT_APP_FIREBASE_ENV;
      let scriptUrl = 'https://ui.s.unit.sh/release/latest/components-extended.js';
      if (env === 'production') {
        scriptUrl = 'https://ui.unit.co/release/latest/components-extended.js';
      }
      
      // Check if script is already loaded globally (don't remove/re-add if already loaded)
      const existingScript = document.getElementById(scriptId) as HTMLScriptElement;
      let script: HTMLScriptElement;
      
      if (existingScript && existingScript.src.includes(scriptUrl)) {
        // Script already exists, reuse it
        console.log('♻️ [UnitWhiteLabelApp] Reusing existing script');
        script = existingScript;
        
        // Check if Unit.co custom elements are already defined (script is loaded)
        // This indicates the script has finished loading
        if (window.customElements && window.customElements.get('unit-elements-white-label-app')) {
          // Script already loaded, create element directly
          createUnitElement();
          return;
        }
        
        // Script exists but not loaded yet, wait for it to load
        // Remove existing onload handler and add new one
        script.onload = () => {
          createUnitElement();
        };
      } else {
        // Remove old script if it exists with different URL
        if (existingScript) {
          existingScript.remove();
        }
        
        // Clear container only if we're creating a new script
        if (containerRef.current) {
          containerRef.current.innerHTML = '';
        }
        
        // Create new script
        script = document.createElement('script');
        script.id = scriptId;
        script.src = scriptUrl; // Remove cache-busting to allow script reuse
        script.async = true;
        
        // Create Unit.co element after script loads
        script.onload = () => {
          createUnitElement();
        };
        
        // Add error handler for script load failures
        script.onerror = () => {
          console.error('❌ UnitWhiteLabelApp: Failed to load Unit.co script');
          initializationKeyRef.current = ''; // Reset on error
          if (containerRef.current) {
            containerRef.current.innerHTML = '<div class="text-center p-4"><p class="text-red-600">Failed to load banking interface. Please refresh the page.</p></div>';
          }
        };
        
        document.body.appendChild(script);
      }
      
      // Helper function to create the Unit.co element
      function createUnitElement() {
        try {
          if (!containerRef.current) {
            console.error('❌ UnitWhiteLabelApp: Container ref is null');
            return;
          }
          
          // CRITICAL: Force remove any existing element completely
          // This ensures widget re-initializes with new token/artist
          const existingElement = containerRef.current.querySelector('unit-elements-white-label-app');
          if (existingElement) {
            console.log('🔄 [UnitWhiteLabelApp] Removing existing element to force re-initialization');
            // Remove event listeners and element completely
            existingElement.remove();
            // Also clear innerHTML to ensure nothing remains
            containerRef.current.innerHTML = '';
            // Reset initialization key
            initializationKeyRef.current = '';
          } else {
            // Clear container before creating new element
            containerRef.current.innerHTML = '';
          }
          
          // CRITICAL: Add a longer delay to ensure DOM is clean and Unit.co SDK resets
          // This prevents Unit.co SDK from reusing cached state
          // Increased delay to 300ms to ensure complete cleanup
          setTimeout(() => {
            try {
              if (!containerRef.current) {
                return;
              }
              
              // Triple-check container is empty and force cleanup if needed
              const stillExists = containerRef.current.querySelector('unit-elements-white-label-app');
              if (stillExists) {
                console.log('⚠️ [UnitWhiteLabelApp] Element still exists after removal, forcing aggressive cleanup');
                try {
                  // Remove all attributes
                  while (stillExists.attributes.length > 0) {
                    stillExists.removeAttribute(stillExists.attributes[0].name);
                  }
                  stillExists.remove();
                } catch (e) {
                  console.error('[UnitWhiteLabelApp] Error removing existing element:', e);
                }
                containerRef.current.innerHTML = '';
                // Force reflow
                void containerRef.current.offsetHeight;
              }
              
              console.log('🔄 [UnitWhiteLabelApp] Creating widget element for artist:', currentArtistId);
              
              // CRITICAL: Create a completely new element with unique ID to force widget reset
              const uniqueId = `unit-widget-${currentArtistId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
              
              // Create Unit.co element with fresh token
              const el = document.createElement('unit-elements-white-label-app');
              
              // CRITICAL: Set unique ID FIRST before any other attributes
              el.id = uniqueId;
              
              // CRITICAL: Set JWT token - this MUST be the token from API response (passed as prop)
              el.setAttribute('jwt-token', jwtToken);
              
              // CRITICAL: Verify the token was set correctly
              const verifyToken = el.getAttribute('jwt-token');
              if (verifyToken !== jwtToken) {
                console.error('❌ [UnitWhiteLabelApp] JWT token mismatch after setting');
                // Force set again
                el.setAttribute('jwt-token', jwtToken);
              }
              
              // CRITICAL: Force widget to recognize this as a new session by adding timestamp
              const sessionId = `${currentArtistId}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
              el.setAttribute('data-session-id', sessionId);
              
              // CRITICAL: Add artist ID to data attribute to force widget to recognize artist change
              if (currentArtistId !== null && currentArtistId !== undefined) {
                el.setAttribute('data-artist-id', currentArtistId.toString());
                // Also add as a data attribute with timestamp to force recognition
                el.setAttribute('data-artist-timestamp', `${currentArtistId}-${Date.now()}`);
              }
              if (propCurrentUserId && typeof propCurrentUserId === 'string') {
                el.setAttribute('data-user-id', propCurrentUserId);
              }
              
            
              if (themeUrl && typeof themeUrl === 'string') {
                el.setAttribute('theme', themeUrl);
              }
              if (languageUrl && typeof languageUrl === 'string') {
                el.setAttribute('language', languageUrl);
              }
              
              console.log('🔄 [UnitWhiteLabelApp] Creating new Unit.co element with JWT token for artist:', currentArtistId);
              
              // Add event listener with error handling (only once)
              el.addEventListener('unitOnLoad', function unitOnLoadHandler(e) {
                try {
              const event = e as CustomEvent;
              if (event.detail?.errors) {
                console.error('❌ Unit.co error:', event.detail.errors);
                
                // Log detailed error information
                const errors = Array.isArray(event.detail.errors) ? event.detail.errors : [event.detail.errors];
                errors.forEach((error: any, index: number) => {
                  console.error(`❌ Unit.co Error ${index + 1}:`, {
                    error,
                    status: error?.status,
                    message: error?.message,
                    detail: error?.detail,
                    timestamp: new Date().toISOString(),
                    userId: propCurrentUserId
                  });
                });
                
                // Handle 401 errors (token expired)
                if (errors[0]?.status === '401' || errors[0]?.status === 401) {
                  console.error('🚨 UnitWhiteLabelApp: Token expired, clearing tokens and reloading page');
                  
                  // Clear user-specific tokens
                  if (propCurrentUserId) {
                    localStorage.removeItem(`unit_jwt_token_${propCurrentUserId}`);
                    localStorage.removeItem(`unit_jwt_exp_${propCurrentUserId}`);
                    localStorage.removeItem(`unitCustomerToken_${propCurrentUserId}`);
                    localStorage.removeItem(`apiType_${propCurrentUserId}`);
                    localStorage.removeItem('unitCustomerToken');
                  }
                  
                  // Reload page to get fresh token
                  window.location.reload();
                  return;
                }
                
                // Handle 500 errors - show user-friendly message
                if (errors[0]?.status === '500' || errors[0]?.status === 500) {
                  console.error('🚨 UnitWhiteLabelApp: Server error (500), showing error message');
                  
                  if (containerRef.current) {
                    containerRef.current.innerHTML = `
                      <div class="text-center p-6">
                        <p class="text-red-600 text-lg font-semibold mb-2">Unable to load banking interface</p>
                        <p class="text-gray-600 text-sm mb-4">The banking service is temporarily unavailable. Please try again in a moment.</p>
                        <button 
                          onclick="window.location.reload()" 
                          class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                        >
                          Reload Page
                        </button>
                      </div>
                    `;
                  }
                  
                  // Dispatch error event for tracking
                  window.dispatchEvent(new CustomEvent('unitcoPluginError', {
                    detail: {
                      type: 'server_error',
                      status: 500,
                      errors: errors,
                      timestamp: new Date().toISOString(),
                      userId: propCurrentUserId
                    }
                  }));
                  
                  return;
                }
              }
              // Use ref to call callback without adding it to dependencies
              if (onLoadCompleteRef.current) {
                onLoadCompleteRef.current();
              }
            } catch (err) {
              console.error('❌ UnitWhiteLabelApp: Error in unitOnLoad handler:', err);
            }
          });
          
            // Add general error event listener
            el.addEventListener('error', function (e: Event) {
              console.error('❌ UnitWhiteLabelApp: Element error event:', e);
              window.dispatchEvent(new CustomEvent('unitcoPluginError', {
                detail: {
                  type: 'element_error',
                  event: e,
                  timestamp: new Date().toISOString(),
                  userId: propCurrentUserId
                }
              }));
            });
            
            // Append to container
            if (containerRef.current) {
              containerRef.current.appendChild(el);
              console.log('✅ [UnitWhiteLabelApp] Element appended to container');
              
              // CRITICAL: Force widget to recognize JWT token change by re-setting it after append
              // Some custom elements only react to attribute changes after they're connected to DOM
              setTimeout(() => {
                if (el.isConnected && el.parentElement === containerRef.current) {
                  console.log('[UnitWhiteLabelApp] Re-setting JWT token after DOM connection to force widget update');
                  // Remove and re-add the attribute to force change detection
                  const currentToken = el.getAttribute('jwt-token');
                  if (currentToken === jwtToken) {
                    // Token is correct, but force widget to re-read it
                    el.removeAttribute('jwt-token');
                    el.setAttribute('jwt-token', jwtToken);
                    console.log('[UnitWhiteLabelApp] JWT token re-set to force widget recognition');
                  } else {
                    console.warn('[UnitWhiteLabelApp] JWT token mismatch after DOM reconnection');
                    el.setAttribute('jwt-token', jwtToken);
                  }
                }
              }, 50); // Small delay after append
            }
            
              // Mark as initialized by updating the key
              initializationKeyRef.current = currentInitKey;
              console.log('✅ [UnitWhiteLabelApp] Successfully initialized with JWT token for artist:', currentArtistId);
              console.log('✅ [UnitWhiteLabelApp] Initialization key set to:', initializationKeyRef.current);
            } catch (err) {
              console.error('❌ UnitWhiteLabelApp: Error creating Unit.co element:', err);
              initializationKeyRef.current = ''; // Reset on error
            }
          }, 300); // Longer delay (300ms) to ensure DOM is clean and Unit.co SDK resets internal state
      } catch (err) {
        console.error('❌ UnitWhiteLabelApp: Error in createUnitElement:', err);
        initializationKeyRef.current = ''; // Reset on error
      }
    }
    
    } catch (err) {
      // alert('Error in useEffect:' + (err as Error).toString());
      console.error('❌ UnitWhiteLabelApp: Error in useEffect:', err);
      initializationKeyRef.current = ''; // Reset on error
    }
    
    // Cleanup function - force cleanup when artist or token changes
    return () => {
      // alert('Cleanup started');
      try {
        console.log('[UnitWhiteLabelApp] Cleanup - removing widget element for artist:', currentArtistId);
        if (containerRef.current) {
          const existingElement = containerRef.current.querySelector('unit-elements-white-label-app');
          if (existingElement) {
            console.log('[UnitWhiteLabelApp] Removing existing widget element in cleanup');
            
            // CRITICAL: Try to disconnect/dispose the widget
            try {
              if ('disconnectedCallback' in existingElement && typeof (existingElement as any).disconnectedCallback === 'function') {
                (existingElement as any).disconnectedCallback();
              }
              
              // Remove all attributes
              while (existingElement.attributes.length > 0) {
                existingElement.removeAttribute(existingElement.attributes[0].name);
              }
            } catch (e) {
              console.log('[UnitWhiteLabelApp] Error during cleanup disconnect:', e);
            }
            
            existingElement.remove();
          }
          containerRef.current.innerHTML = '';
          // Force reflow
          void containerRef.current.offsetHeight;
        }
        const script = document.getElementById('unit-wl-script');
        if (script) script.remove();
        removeUnitCoLauncher();
        // alert('Cleanup complete');
        initializationKeyRef.current = ''; // Reset initialization key on cleanup
      } catch (err) {
        alert('Error in cleanup:' + (err as Error).toString());
        console.error('❌ UnitWhiteLabelApp: Error in cleanup:', err);
      }
    };
  }, [jwtToken, propCurrentUserId, currentArtistId, themeUrl, languageUrl]); // CRITICAL: Include currentArtistId to force re-initialization


  // Show loading state if we don't have both requirements
  if (!propCurrentUserId || !jwtToken) {
    return (
      <div className={`rounded-2xl p-6 bg-white ${cardType === "available-balance" ? "min-h-[200px]" : "shadow-lg min-h-[600px]"} overflow-hidden relative`}>
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-600 text-sm">
              {!propCurrentUserId ? 'Loading user...' : 'Loading banking...'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`rounded-2xl p-6 bg-white ${cardType === "available-balance" ? "min-h-[200px]" : "shadow-lg min-h-[600px]"} overflow-hidden relative`}>
      {/* Always render Unit Elements container */}
      <div
        ref={containerRef}
        className={`w-full h-full ${cardType === "available-balance" ? "absolute opacity-0 pointer-events-none" : "overflow-auto"}`}
        style={cardType === "available-balance" ? { zIndex: -1 } : {}}
      />
      
      {cardType === "available-balance" && (
        // Show loading state for available-balance
        <div className="flex items-center justify-center h-full">
          <div className="text-center">
            <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
            <p className="text-gray-600 text-sm">Loading...</p>
          </div>
        </div>
      )}
    </div>
  );
  
};

export default UnitWhiteLabelApp;
